import { useState, useLayoutEffect, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhoenixLogo } from '../components/PhoenixLogo';
import { BookingModal } from '../components/BookingModal';
import { completeOnboarding, getBooking } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { C } from '../utils/constants';

const TOTAL_PAGES = 6;

const PageDots = ({ current, total }) => (
  <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 32 }}>
    {Array.from({ length: total }, (_, i) => (
      <div
        key={i}
        style={{
          width: i === current ? 24 : 8,
          height: 8,
          borderRadius: 4,
          background: i === current
            ? `linear-gradient(90deg, ${C.amber}, ${C.gold})`
            : C.border,
          transition: 'all 0.3s ease',
        }}
      />
    ))}
  </div>
);

export const Onboarding = () => {
  const navigate = useNavigate();
  const { user, markOnboarded } = useAuth();
  const [page, setPage] = useState(0);
  const [showBooking, setShowBooking] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [isBooked, setIsBooked] = useState(false);

  const fetchBooking = useCallback(async () => {
    try {
      const res = await getBooking();
      if (res.data?.callBooked) { setIsBooked(true); return true; }
    } catch (e) { /* not critical */ }
    return false;
  }, []);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  const pollForBooking = useCallback(() => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      const booked = await fetchBooking();
      if (booked || attempts >= 10) clearInterval(interval);
    }, 3000);
    return () => clearInterval(interval);
  }, [fetchBooking]);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  // Intercept browser back button to navigate within onboarding
  const pageRef = useRef(page);
  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => {
    window.history.pushState(null, '');
    const handlePopState = () => {
      if (pageRef.current > 0) {
        setPage(p => p - 1);
        window.history.pushState(null, '');
      } else {
        navigate('/login');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const firstName = user?.first_name || 'there';

  const handleBeginDay1 = async () => {
    setCompleting(true);
    try {
      await completeOnboarding();
    } catch (e) {
      // Not onboarded on the server, so Day 1 stays closed (App.jsx
      // CourseGate). Stay here with the button live again so they can retry,
      // rather than going to a dashboard that would send them straight back.
      console.error(e);
      setCompleting(false);
      return;
    }
    markOnboarded();
    navigate('/dashboard', { replace: true });
  };

  const cardStyle = {
    background: '#fff',
    borderRadius: 20,
    padding: '36px 32px',
    maxWidth: 560,
    width: '100%',
    margin: '0 auto',
    boxShadow: '0 8px 40px rgba(61,31,2,0.12)',
    animation: 'fadeInUp 0.4s ease both',
  };

  const itemBadge = (icon, text, subtext) => (
    <div key={text} style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 16,
      padding: '16px 20px',
      background: C.veryLightBrown,
      borderRadius: 12,
      border: `1px solid ${C.border}`,
      marginBottom: 12,
    }}>
      <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>{icon}</span>
      <div>
        <div style={{ fontWeight: 700, color: C.brown, marginBottom: 2 }}>{text}</div>
        <div style={{ fontSize: '0.95rem', color: C.terracotta }}>{subtext}</div>
      </div>
    </div>
  );

  const stepItem = (num, title, desc) => (
    <div key={num} style={{ display: 'flex', gap: 16, marginBottom: 20, alignItems: 'flex-start' }}>
      <div style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${C.amber}, ${C.gold})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Forum', serif",
        color: '#fff',
        fontWeight: 700,
        fontSize: '1rem',
        flexShrink: 0,
        marginTop: 2,
      }}>
        {num}
      </div>
      <div>
        <div style={{ fontWeight: 700, color: C.brown, marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: '1rem', color: C.text, lineHeight: 1.5 }}>{desc}</div>
      </div>
    </div>
  );

  const pages = [
    // Page 0: Welcome
    <div key={0} style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
        <PhoenixLogo size={180} />
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          fontFamily: "'Forum', serif",
          fontSize: '0.72rem',
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: C.gold,
          marginBottom: 8,
        }}>
          Welcome to Your Journey
        </div>
        <h1 style={{
          fontFamily: "'Forum', serif",
          color: C.brown,
          fontSize: '1.9rem',
          letterSpacing: '0.03em',
          marginBottom: 16,
        }}>
          Congratulations, {firstName}! 🎉
        </h1>
        <p style={{ color: C.text, lineHeight: 1.7, marginBottom: 16, fontSize: '1.05rem' }}>
          You've made a powerful decision by joining <strong>Recondition Yourself</strong>. This isn't just a program — it's the beginning of a deep, lasting transformation.
        </p>
        <p style={{ color: C.text, lineHeight: 1.7, fontSize: '1.05rem' }}>
          Over the next 7 days, you'll break old conditioning, reconnect with your authentic self, and step into a life of real freedom and purpose.
        </p>
      </div>
      <PageDots current={0} total={TOTAL_PAGES} />
      <button
        className="btn-primary"
        onClick={() => setPage(1)}
        style={{ width: '100%', marginTop: 28 }}
      >
        Let's Begin →
      </button>
    </div>,

    // Page 1: What You've Got
    <div key={1} style={cardStyle}>
      <div style={{
        fontFamily: "'Forum', serif",
        fontSize: '0.72rem',
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: C.gold,
        marginBottom: 8,
      }}>
        What's Inside
      </div>
      <h2 style={{
        fontFamily: "'Forum', serif",
        color: C.brown,
        fontSize: '1.6rem',
        marginBottom: 24,
      }}>
        What You've Got
      </h2>

      {itemBadge('🎬', '7 Powerful Training Videos', 'One per day — watch, absorb, and integrate at your own pace')}
      {itemBadge('📝', 'Daily Journalling Prompts', 'Deep reflection questions to help you go within and unlock insight')}
      {itemBadge('🧘', 'Guided Meditations', 'Powerful meditations on select days to anchor your transformation')}

      <PageDots current={1} total={TOTAL_PAGES} />
      <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
        <button className="btn-secondary" onClick={() => setPage(0)} style={{ flex: 1 }}>← Back</button>
        <button className="btn-primary" onClick={() => setPage(2)} style={{ flex: 2 }}>Next →</button>
      </div>
    </div>,

    // Page 2: Book Your Session
    <div key={2} style={cardStyle}>
      <div style={{
        fontFamily: "'Forum', serif",
        fontSize: '0.72rem',
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: C.gold,
        marginBottom: 8,
      }}>
        1:1 With Ben
      </div>
      <h2 style={{
        fontFamily: "'Forum', serif",
        color: C.brown,
        fontSize: '1.6rem',
        marginBottom: 16,
      }}>
        Book Your Session
      </h2>
      <p style={{ color: C.text, lineHeight: 1.7, marginBottom: 20, fontSize: '1.05rem' }}>
        As part of your program, you have a <strong>90-minute Breakthrough Mapping Session</strong> with Ben — completely included. This is where real breakthroughs happen.
      </p>
      <div style={{
        background: `linear-gradient(135deg, ${C.darkBrown}08, ${C.brown}12)`,
        border: `1.5px solid ${C.border}`,
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 24,
      }}>
        <div style={{ fontFamily: "'Forum', serif", color: C.brown, fontWeight: 700, marginBottom: 6 }}>
          🎯 Breakthrough Mapping Session
        </div>
        <div style={{ fontSize: '0.95rem', color: C.terracotta, lineHeight: 1.6 }}>
          90 minutes · Via Zoom · With Ben<br />
          Tailored to your unique patterns and goals
        </div>
      </div>
      <button
        className="btn-primary"
        onClick={() => setShowBooking(true)}
        style={{ width: '100%', marginBottom: 12 }}
      >
        📅 Book My Session
      </button>
      <PageDots current={2} total={TOTAL_PAGES} />
      <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
        <button className="btn-secondary" onClick={() => setPage(1)} style={{ flex: 1 }}>← Back</button>
        <button className="btn-primary" onClick={() => setPage(3)} style={{ flex: 2, ...(isBooked ? {} : { background: 'transparent', color: C.brown, border: `1.5px solid ${C.brown}` }) }}>
          {isBooked ? 'Next →' : 'Skip for now →'}
        </button>
      </div>
    </div>,

    // Page 3: How It Works
    <div key={3} style={cardStyle}>
      <div style={{
        fontFamily: "'Forum', serif",
        fontSize: '0.72rem',
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: C.gold,
        marginBottom: 8,
      }}>
        Your Path
      </div>
      <h2 style={{
        fontFamily: "'Forum', serif",
        color: C.brown,
        fontSize: '1.6rem',
        marginBottom: 24,
      }}>
        How It Works
      </h2>

      {stepItem(1, 'Watch the Daily Video', 'Each day unlocks a new training module. Watch fully to unlock journalling.')}
      {stepItem(2, 'Journal Your Insights', 'Answer the reflection prompts honestly — this is where change happens.')}
      {stepItem(3, 'Complete the Meditation', 'On select days, a guided meditation will deepen your integration.')}
      {stepItem(4, 'Unlock the Next Day', 'Complete each day and the next unlocks automatically.')}

      <PageDots current={3} total={TOTAL_PAGES} />
      <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
        <button className="btn-secondary" onClick={() => setPage(2)} style={{ flex: 1 }}>← Back</button>
        <button className="btn-primary" onClick={() => setPage(4)} style={{ flex: 2 }}>Next →</button>
      </div>
    </div>,

    // Page 4: Community
    <div key={4} style={cardStyle}>
      <div style={{
        fontFamily: "'Forum', serif",
        fontSize: '0.72rem',
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: C.gold,
        marginBottom: 8,
      }}>
        You're Not Alone
      </div>
      <h2 style={{
        fontFamily: "'Forum', serif",
        color: C.brown,
        fontSize: '1.6rem',
        marginBottom: 16,
      }}>
        Join the Community
      </h2>
      <p style={{ color: C.text, lineHeight: 1.7, marginBottom: 24, fontSize: '1.05rem' }}>
        Join a private community of people doing this work alongside you. Share insights, get support, and celebrate each other's wins.
      </p>
      <a
        href="https://www.facebook.com/groups/reconditionyourself"
        target="_blank"
        rel="noopener noreferrer"
        className="btn-primary"
        style={{
          display: 'block',
          width: '100%',
          textAlign: 'center',
          marginBottom: 24,
          background: '#1877F2',
        }}
      >
        Join The Recondition Yourself Community Facebook Group →
      </a>
      <PageDots current={4} total={TOTAL_PAGES} />
      <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
        <button className="btn-secondary" onClick={() => setPage(3)} style={{ flex: 1 }}>← Back</button>
        <button className="btn-primary" onClick={() => setPage(5)} style={{ flex: 2 }}>Next →</button>
      </div>
    </div>,

    // Page 5: Fully Onboarded
    <div key={5} style={cardStyle}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>🔥</div>
        <div style={{
          fontFamily: "'Forum', serif",
          fontSize: '0.72rem',
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: C.gold,
          marginBottom: 8,
        }}>
          You're Ready
        </div>
        <h2 style={{
          fontFamily: "'Forum', serif",
          color: C.brown,
          fontSize: '1.7rem',
          marginBottom: 16,
        }}>
          You're Fully Onboarded!
        </h2>
      </div>

      <div style={{
        background: C.veryLightBrown,
        borderRadius: 12,
        padding: '20px 24px',
        marginBottom: 24,
        borderLeft: `4px solid ${C.gold}`,
      }}>
        <p style={{ color: C.text, lineHeight: 1.75, fontSize: '1.05rem', fontStyle: 'italic' }}>
          "You've just made a powerful statement to yourself: I matter enough to begin.
          <br /><br />
          Now give yourself permission to keep saying: I matter enough to continue."
        </p>
        <p style={{
          fontFamily: "'Forum', serif",
          color: C.brown,
          marginTop: 12,
          fontSize: '0.88rem',
          letterSpacing: '0.04em',
        }}>
          — Ben Milston
        </p>
      </div>

      <p style={{ color: C.terracotta, fontSize: '0.85rem', textAlign: 'center', marginBottom: 24 }}>
        Day 1 is waiting for you.<br />Your transformation starts now.
      </p>

      <PageDots current={5} total={TOTAL_PAGES} />
      <button
        className="btn-primary"
        onClick={handleBeginDay1}
        disabled={completing}
        style={{ width: '100%', marginTop: 28, fontSize: '1.05rem' }}
      >
        {completing ? 'Loading...' : 'Begin Day 1 →'}
      </button>
    </div>,
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(160deg, ${C.cream} 0%, ${C.veryLightBrown} 100%)`,
      padding: '32px 16px 60px',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{
          fontFamily: "'Forum', serif",
          fontSize: '0.7rem',
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: C.terracotta,
          opacity: 0.7,
        }}>
          Step {page + 1} of {TOTAL_PAGES}
        </div>
      </div>

      {pages[page]}

      {showBooking && <BookingModal onClose={() => { setShowBooking(false); pollForBooking(); }} daysCompleted={0} />}
    </div>
  );
};
